import { FC, ReactElement } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import LinkIcon from '@mui/icons-material/Link';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { Attachment } from '../../services/attachmentService';

interface HyperlinkListItemProps {
    attachment: Attachment;
    allowModifications: boolean;
    canDelete: boolean;
    onDelete: (attachment: Attachment) => void;
}

export const HyperlinkListItem: FC<HyperlinkListItemProps> = ({
    attachment,
    allowModifications,
    canDelete,
    onDelete,
}): ReactElement => {
    const urlValue = attachment.url || '';
    const url = urlValue.startsWith('http') ? urlValue : `https://${urlValue}`;
    const display = attachment.displayName || urlValue;
    
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                px: 2,
                py: 1.5,
                '&:hover': {
                    backgroundColor: '#F9FAFB',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                <LinkIcon sx={{ fontSize: 20, color: '#667085' }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: '#6B4C9A',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {display}
                    </Typography>
                    <Typography
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            fontSize: '0.6875rem',
                            color: '#667085',
                            textDecoration: 'none',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {urlValue}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                    size="small"
                    href={url}
                    target="_blank"
                    sx={{
                        color: '#98A2B3',
                        '&:hover': { color: '#1976d2', backgroundColor: '#EFF6FF' },
                    }}
                >
                    <AttachFileOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
                {allowModifications && canDelete && (
                    <IconButton
                        size="small"
                        onClick={() => onDelete(attachment)}
                        sx={{
                            color: '#98A2B3',
                            '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' },
                        }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};
